import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import {
    Box, Button, Card, FormControl, MenuItem, Select, TextField,
    Table, TableBody, TableCell, TableHead, TableRow,
    Checkbox, Autocomplete, InputLabel, Paper, Modal, Typography,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    Tooltip, ListItem, ListItemText, IconButton,
} from "@mui/material";
import RestAPIService from "../../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../../Utility/API/Serviceurls";
import ShowComponents from "../Common/ShowComponent";
import { Colors } from "../../../../Styles/Colors";
import { renderTitle } from "../Common/DocCommonComponent";
import { renderIconButton } from "../Common/DocCommonComponent";
import { instructionList, duration_type, language, uomUnitOptions } from "../../../../Utility/Constants";
import { debounce, formatDate, ContentInTooltip } from '../../../../Components/CommonFunctions/CommonFunctions';
import { ImagePaths } from '../../../../Utility/ImagePaths';
import DeleteIcon from '@mui/icons-material/Delete';
import { ToolsFieldWithMic, DeletePopup } from "../../../../Components/Common Components/CommonComponents";
import CommonValidation from "../../../../Components/CommonFunctions/CommonValidation";
import { localGetItem } from "../../../../Utility/Services/CacheProviderService";

const headers = ["Medicine Name", "UOM", "M  A  E  N", "Instruction", "Duration", "Action"]
const colWidths = ["14.5dvw", "10dvw", "6dvw", "16dvw", "14dvw", "6.5dvw"];

const default_sub_drug = {
    drug_ms_id: null,
    drug_name: "",
    quantity: "",
    uom: "",
    medicineList: [], // searched medicine list
    in_stock: 0,
    isSubDrug: false,
    isSubDrugDel: false,
}

const cellPadding = "0.25dvw 0.3dvw";

const default_line_item =
{
    morning: false,
    afternoon: false,
    evening: false,
    night: false,
    instructions: "",
    duration: "",
    duration_mode: "1",
    comments: "",
    drugs: [default_sub_drug],
    showComments: false,
    isSaving: false,
    isDeleting: false,
}

const preparePrescriptionListForAPI = (prescriptionList, fromTemplate = "") => {
    let processedList = prescriptionList?.map(row => {
        // Ensure row has id
        const rowId = fromTemplate === "Add" ? null : row.id ?? null;

        // Process drugs
        const processedDrugs = (row.drugs || [])
            ?.filter(drug => drug.drug_ms_id)
            ?.map(drug => {
                return {
                    id: fromTemplate === "Add" ? null : drug.id ?? null,
                    drug_ms_id: drug.drug_ms_id,
                    quantity: drug.quantity,
                    uom: drug.uom,
                    is_favorite: false,
                };
            });

        // Build row object for API
        return {
            id: rowId,
            morning: row?.morning || false,
            afternoon: row?.afternoon || false,
            evening: row?.evening || false,
            night: row?.night || false,
            instructions: row?.instructions || "",
            duration: +row?.duration,
            duration_mode: +row.duration_mode || 1,
            comments: row?.comments || "",
            drugs: processedDrugs,
        };
    });

    // Validation: check that all rows have at least one drug
    processedList = processedList?.filter(row => row.drugs.length > 0);
    const allRowsHaveDrugs = processedList.every(row => row.drugs.length > 0);

    // Return processed list only if all rows have drugs, else return empty array
    return allRowsHaveDrugs ? processedList : [];
};

const prepareAddKeyList = (list) => {
    return list?.map(row => {
        // Process drugs
        const processedDrugs = (row.drugs || [])
            ?.map(drug => {
                return {
                    ...drug,
                    in_stock: 0,
                    isSubDrug: false,
                    isSubDrugDel: false,
                    medicineList: [drug],
                };
            });
        return {
            ...row,
            drugs: processedDrugs,
            showComments: Boolean(row?.comments),
            isSaving: false,
            isDeleting: false,
        };
    });
}

const DocPrescription = ({ Title, successMessage, errorMessage, ...props }) => {
    const { clinicId } = props;

    const [state, setState] = useState({
        prescriptionRow: default_line_item,
        prescriptionList: [],
        selectedPharmacy: null,
        pharmacyList: [],
        prescriptionTemplateList: [],
        selectedTemplate: null,
        tempType: 'Add',
        selectedLanguage: 'english',
        overallComments: "",
        deleteTemplateName: null,
        deletePopup: false,
        openPopupAddNew: false,
        removeData: false,
        isTransClked: false,
        successPopup: false,
        successPopupTxt: '',
        smsSuccessTxt: '',
        recallPopup: false,
        recallReason: '',
        recallError: '',
        isLoading: false,
        transmitted: false,
        prescriptionId: null,
        selectedPrevDate: null,
    });

    const editorRef = useRef(null);
    const commonInputSx = {
        height: 40,
        '& .MuiInputBase-root': {
            height: 40,
        },
    };

    const updateState = (obj) => setState((prev) => ({ ...prev, ...obj }));

    const setEditorContent = (html) => {
        try {
            if (
                editorRef.current &&
                editorRef.current.editorRef &&
                editorRef.current.editorRef.current
            ) {
                editorRef.current.editorRef.current.innerHTML = html || "";
            }
        } catch (err) {
            console.warn("Editor content update failed:", err);
        }
    };

    const getApiCall = useCallback(() => {
        getPharmacyList()
        getPrescriptionData(true)
    }, [clinicId]);



    // useeffect of transmit, prev prescription
    useEffect(() => {
        if (state.isTransClked && state.selectedPharmacy) {
            postPrescriptionTransmit()
        }
    }, [state.isTransClked, state.selectedPharmacy])

    // get api calls start 
    const getPrescriptionData = (fromInitialGet = false) => {
        const { appointmentId, ipAdmissionId, clinicId, doctorId, isFromIp } = props;
        if (!isFromIp && !appointmentId) return;
        if (isFromIp && !ipAdmissionId) return;
        try {
            const params = isFromIp ? `?ip_admission_id=${ipAdmissionId}&clinic_id=${clinicId}&doctor_id=${doctorId}` : `?appointment_id=${appointmentId}`;
            RestAPIService.getAll(`${Serviceurls.AYURVEDA_PRESCRIPTION_LIST}${params}`)
                .then((response) => {
                    if (response.data.status === "success") {
                        const { data = [], comments = "", is_billed = false,
                            is_transmitted = false, print_language = "english",
                            id = null, pharmacy_id } = response?.data?.data || {};

                        const processedList = prepareAddKeyList(data) || [];
                        updateState({
                            prescriptionList: [...processedList],
                            selectedPharmacy: pharmacy_id ?? state.selectedPharmacy ?? state.pharmacyList[0]?.pharmacy_id,
                            prescriptionId: id || null,
                            Recalled: is_billed || false,
                            transmitted: is_transmitted || false,
                            overallComments: comments,
                            selectedLanguage: print_language?.toLowerCase()
                        });
                        if (fromInitialGet) {
                            getTemplateListData(pharmacy_id ?? state.selectedPharmacy)
                        }
                        setEditorContent(comments);
                    } else {
                        errorMessage(response.data.message)
                    }
                }).catch((error) => {
                    errorMessage(error.message)
                })
        }
        catch (e) {
            errorMessage(e.message)
        }
    }

    const getPharmacyList = () => {
        try {
            RestAPIService.getAll(Serviceurls.AYURVEDA_PHARMACY_LIST)
                .then((response) => {
                    if (response.data.status === "success") {
                        const Data = response?.data?.data || []
                        updateState({
                            pharmacyList: Data,
                            selectedPharmacy: Data[0]?.pharmacy_id ?? null
                        })
                    } else {
                        errorMessage(response.data.message)
                    }
                }).catch((error) => {
                    errorMessage(error.message)
                })
        }
        catch (e) {
            errorMessage(e.message)
        }
    }

    const getPrescriptionPrint = (prevAppointmentId = null) => {
        const { appointmentId, ipAdmissionId, clinicId, doctorId, isFromIp } = props;
        if (!isFromIp && !appointmentId && !prevAppointmentId) return;
        if (isFromIp && !ipAdmissionId) return;
        try {
            const idParam = isFromIp ? `ip_admission_id=${ipAdmissionId}&clinic_id=${clinicId}&doctor_id=${doctorId}` : `appointment_id=${prevAppointmentId || appointmentId || ""}`;
            RestAPIService.getAllTwo(Serviceurls.AYURVEDA_PRESCRIPTION_LIST +
                `?language=${state.selectedLanguage}&${idParam}&export_type=pdf`)
                .then((response) => {
                    const file = new Blob(
                        [response.data],
                        { type: 'application/pdf' });
                    const fileURL = URL.createObjectURL(file);
                    window.open(fileURL);
                }).catch((error) => {
                    errorMessage(error?.response?.data?.message || error.message)
                })
        } catch (error) {
            errorMessage(error.message)
        }
    }
    // get api calls end 

    const debouncedSearch = useCallback(
        debounce(async ({ value, rowIndex, subIndex, selectedPharmacy, isNewPrescription = false }) => {
            try {
                if (!selectedPharmacy) {
                    errorMessage("Kindly select a pharmacy");
                    return;
                }
                const response = await RestAPIService.getAll(`${Serviceurls.AYURVEDA_DRUG_SEARCH}?pharmacy_id=${selectedPharmacy}&brand_name=${value}&generic_name=`)
                const results = response.data.data || [];

                setState(prev => {
                    if (isNewPrescription) {
                        // Update newPrescription
                        const updatedDrugs = [...prev.prescriptionRow.drugs];
                        updatedDrugs[subIndex] = {
                            ...updatedDrugs[subIndex],
                            medicineList: results
                        };

                        return {
                            ...prev,
                            prescriptionRow: {
                                ...prev.prescriptionRow,
                                drugs: updatedDrugs
                            }
                        };
                    } else {
                        const updatedList = [...prev.prescriptionList];
                        const updatedRow = { ...updatedList[rowIndex] };
                        const updatedDrugs = [...updatedRow.drugs];
                        const updatedDrugItem = { ...updatedDrugs[subIndex] };

                        updatedDrugItem.medicineList = results;

                        updatedDrugs[subIndex] = updatedDrugItem;
                        updatedRow.drugs = updatedDrugs;
                        updatedList[rowIndex] = updatedRow;

                        return { ...prev, prescriptionList: updatedList };
                    }
                });

            } catch (e) {
                console.error(e);
            }
        }, 300),
        []
    );

    const convertInStock = (stock, uom) => {
        if (!stock) return 0;

        switch (uom) {
            case "kg":
            case "l":
                // Assuming stock comes in grams, convert to kg
                // Assuming stock comes in ml, convert to liters
                return stock / 1000;
            default:
                return stock; // Nos, g, ml remain as-is
        }
    };

    const handleSelectMedicine = (rowIndex, subIndex, selected, isNewPrescription = false) => {
        try {
            RestAPIService.getAll(`${Serviceurls.AYURVEDA_DRUG_DETAILS}?pharmacy_id=${state.selectedPharmacy}&drug_ms_id=${selected?.drug_ms_id}`)
                .then(response => {
                    if (response.data.status !== "success") {
                        errorMessage(response.data.message);
                        return;
                    }

                    const Data = response.data.data?.[0] || {};
                    const latest = Data.latest_prescription || {};
                    const uom = Data.uom || "";

                    // Common sub-drug update block
                    const buildUpdatedSubDrug = (prevSub) => ({
                        ...prevSub,
                        drug_ms_id: Data?.drug_ms_id || null,
                        drug_name: Data?.drug_name || "",
                        quantity: latest?.quantity || "",
                        uom,
                        in_stock: convertInStock(Data.in_stock, uom),
                        medicineList: prevSub?.medicineList || []
                    });

                    // Common row-level fields
                    const rowExtraFields = {
                        morning: latest?.morning || false,
                        afternoon: latest?.afternoon || false,
                        evening: latest?.evening || false,
                        night: latest?.night || false,
                        instructions: latest?.instructions?.trim() || "",
                        duration: latest?.duration || "",
                        duration_mode: latest?.duration_mode || "1",
                        comments: latest?.comments || "",
                        showComments: Boolean(latest?.comments)
                    };

                    setState(prev => {
                        if (isNewPrescription) {
                            const drugs = [...prev.prescriptionRow.drugs];
                            drugs[subIndex] = buildUpdatedSubDrug(drugs[subIndex]);

                            return {
                                ...prev,
                                prescriptionRow: {
                                    ...prev.prescriptionRow,
                                    drugs,
                                    ...rowExtraFields
                                },
                                selectedPharmacy: Data?.pharmacy_id
                            };
                        }

                        // existing prescriptionList
                        const list = [...prev.prescriptionList];
                        const row = { ...list[rowIndex] };
                        const subDrugs = [...row.drugs];

                        subDrugs[subIndex] = buildUpdatedSubDrug(subDrugs[subIndex]);
                        row.drugs = subDrugs;

                        Object.assign(row, rowExtraFields);
                        list[rowIndex] = row;

                        return {
                            ...prev,
                            prescriptionList: list,
                            selectedPharmacy: Data?.pharmacy_id
                        };
                    });
                })
                .catch(err => errorMessage(err.message));
        } catch (e) {
            errorMessage(e.message);
        }
    };

    const renderAutoComplete = (label, width, rowIndex, subIndex, isNewPrescription = false) => {
        const dataSource = isNewPrescription ? state.prescriptionRow : state.prescriptionList[rowIndex];
        const drugName = dataSource?.drugs[subIndex]?.drug_name || "";

        const selectedValue = dataSource?.drugs[subIndex]?.medicineList
            ?.find((item) => item.drug_name === drugName) || null;

        const rowId = state.prescriptionList?.[rowIndex]?.id ?? rowIndex;
        const acKey = `medicine_${rowId}_${subIndex}`;

        return (
            <Autocomplete
                size="small"
                key={acKey}
                options={dataSource?.drugs[subIndex]?.medicineList || []}
                getOptionLabel={(option) => typeof (option) === 'string' ? option : `${option?.drug_name || ""} - ${option?.generic_name || ""}` || ""}
                value={selectedValue || null}
                onInputChange={(e, newValue, reason) => {
                    if (reason === "input" && newValue) {
                        debouncedSearch({ value: newValue, rowIndex, subIndex, selectedPharmacy: state.selectedPharmacy, isNewPrescription });
                    }
                }}
                onChange={(e, newValue) => {
                    if (newValue) {
                        handleSelectMedicine(rowIndex, subIndex, newValue, isNewPrescription);
                    }
                }}
                renderInput={(params) => (
                    <TextField {...params} label={label} sx={{ width }} />
                )}
            />
        );
    };

    const restrictDuration = (durationMode, value) => {
        let restrictedValue = value;

        // Apply restriction only if the key is 'duration'
        const numValue = Number(value) || 0;

        switch (durationMode) {
            case '1': // Days
            case '5': // Alternative Days
                restrictedValue = Math.max(1, Math.min(numValue, 31));
                break;
            case '2': // Weeks
            case '4': // Years
                restrictedValue = Math.max(1, Math.min(numValue, 15));
                break;
            case '3': // Months
                restrictedValue = Math.max(1, Math.min(numValue, 12));
                break;
            default:
                restrictedValue = numValue;
        }
        return restrictedValue;
    }

    const handleChange = (stateKey, value, rowIndex = null, subIndex = null, isMultiple = false, isNewPrescription = false) => {
        setState(prev => {
            const targetRow = isNewPrescription
                ? { ...prev.prescriptionRow }
                : { ...prev.prescriptionList[rowIndex] };

            const drugs = [...(targetRow.drugs || [])];
            if (subIndex !== null) {
                const item = { ...drugs[subIndex] };

                if (stateKey === "quantity") {
                    const inStock = +item.in_stock;
                    if (value === '' || (CommonValidation.numberOnly(value) && (+value >= 0 && +value <= inStock))) {
                        item.quantity = value;
                    }
                } else {
                    item[stateKey] = value;
                }

                drugs[subIndex] = item;
                targetRow.drugs = drugs;
            } else {
                let newValue = value;

                if (isMultiple && typeof value === "string") {
                    newValue = value.split(",");
                }

                if (stateKey === "duration") {
                    newValue = restrictDuration(targetRow.duration_mode, value) || "";
                }

                targetRow[stateKey] = newValue;
            }
            if (isNewPrescription) {
                return {
                    ...prev,
                    prescriptionRow: targetRow
                };
            } else {
                const rows = [...prev.prescriptionList];
                rows[rowIndex] = targetRow;

                return {
                    ...prev,
                    prescriptionList: rows
                };
            }
        });
    };

    const renderTextFieldRow = (label, stateKey, width = "4dvw", rowIndex, subIndex = null, isNewPrescription = false) => {
        const dataSource = isNewPrescription ? state.prescriptionRow : state.prescriptionList[rowIndex];
        const value = subIndex !== null
            ? dataSource?.drugs[subIndex][stateKey] || ""
            : dataSource[stateKey] || "";
        return (
            <TextField
                size="small"
                autoComplete="off"
                sx={{ width, ...commonInputSx, mr: .5 }}
                label={label}
                value={value}
                onChange={(e) => { handleChange(stateKey, e.target.value, rowIndex, subIndex, false, isNewPrescription) }}
            />
        );
    };

    const renderSelectRow = (label, stateKey, list = [], width = "4dvw", showKey = "label", valueKey = "value", rowIndex, subIndex = null, isMultiple = false, isNewPrescription = false) => {
        const dataSource = isNewPrescription ? state.prescriptionRow : state.prescriptionList[rowIndex];

        const value = subIndex !== null
            ? dataSource?.drugs[subIndex][stateKey] || (isMultiple ? [] : "")
            : dataSource[stateKey] || (isMultiple ? [] : "");

        return (
            <FormControl size="small" sx={{ width }}>
                <InputLabel>{label}</InputLabel>
                <Select
                    sx={{ fontSize: '0.95vw' , ...commonInputSx }}
                    multiple={isMultiple}
                    label={label}
                    size='small'
                    value={value}
                    onChange={(e) => { handleChange(stateKey, e.target.value, rowIndex, subIndex, isMultiple, isNewPrescription) }}
                >
                    {list?.map((item) => (
                        <MenuItem sx={{ fontSize: '0.95vw' }} key={item[valueKey]} value={item[valueKey]}>{item[showKey]}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        )
    }

    const renderCheckBox = (stateKey, rowIndex, value = null, isNewPrescription = false) => {
        const dataSource = isNewPrescription ? state.prescriptionRow : state.prescriptionList[rowIndex];

        return (
            <Checkbox
                size="small"
                disabled={value !== undefined && value !== null}
                sx={{
                    margin: 0,
                    padding: 0,
                    minWidth: 0,
                }}
                checked={value ?? dataSource[stateKey] ?? false}
                onChange={(e) => handleChange(stateKey, e.target.checked, rowIndex, null, false, isNewPrescription)}
            />
        )
    }

    const handleAddToPrescriptionList = () => {
        // Validate all sub-drugs in the new prescription
        const isValid = state.prescriptionRow.drugs?.every(drug =>
            drug?.drug_name &&
            drug?.drug_ms_id &&
            drug?.quantity
        );

        if (!isValid) {
            errorMessage("Please fill all mandatory fields for all sub-drugs (Medicine, Quantity)");
            return;
        }

        // Also validate main prescription fields if needed
        if (!state.prescriptionRow.instructions) {
            errorMessage("Please select a instructions");
            return;
        } else if (!state.prescriptionRow.morning && !state.prescriptionRow.evening && !state.prescriptionRow.afternoon && !state.prescriptionRow.night) {
            errorMessage("Please select a MAEN");
            return;
        }

        setState(prev => {
            const newRow = {
                ...prev.prescriptionRow,
                drugs: [...prev.prescriptionRow.drugs.map(drug => ({ ...drug }))]
            };

            return {
                ...prev,
                prescriptionList: [newRow, ...prev.prescriptionList],
                prescriptionRow: {
                    ...default_line_item,
                    drugs: [{ ...default_sub_drug }]
                }
            };
        });
    };

    const handleEditorSave = (...args) => {
        let content = "";
        if (args.length === 1) {
            content = args[0] || "";
        } else if (args.length >= 2) {
            content = args[1] || "";
        }

        updateState({ overallComments: content });
    };

    const renderSelect = (label, stateKey, list = [], width = "12dvw", showKey = "label", valueKey = "value", disabled = false) => {
        return (
            <FormControl size="small" sx={{ width: width }}>
                <InputLabel id={`${stateKey}-label`}>{label}</InputLabel>
                <Select
                    label={label}
                    labelId={`${stateKey}-label`}
                    sx={{ fontSize: '0.95vw' }}
                    size='small'
                    value={state[stateKey] ?? ""}
                    disabled={Boolean(disabled)}
                    onChange={(e) => updateState({ [stateKey]: e.target.value })}
                >
                    {list?.map((item) => (
                        <MenuItem sx={{ fontSize: '0.95vw' }}
                            key={item[valueKey]}
                            value={item[valueKey]}>{item[showKey]}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        )
    }

    const renderTextField = (label, stateKey, maxLength, width = "12vw") => {
        return (<TextField
            size='small'
            sx={{ width: width }}
            inputProps={{ maxLength: maxLength }}
            value={state[stateKey]}
            label={label}
            autoComplete='off'
            onChange={(e) => updateState({ [stateKey]: e.target.value })}
        />)
    }

    const handleAddSubDrug = (rowIndex, isNewPrescription = false) => {
        setState(prev => {
            // Pick correct target list based on flag
            const target = isNewPrescription ? [...prev.prescriptionRow.drugs] : [...prev.prescriptionList[rowIndex].drugs];

            // Validate existing items
            const isValid = target?.every(item => item?.drug_name && item?.drug_ms_id && item?.quantity);

            if (!isValid) {
                errorMessage("Kindly Fill all mandatory fields");
                return prev;
            }

            // Add new row
            const updated = [...target, { ...default_sub_drug }];

            if (isNewPrescription) {
                // Update newPrescription row
                return {
                    ...prev,
                    prescriptionRow: {
                        ...prev.prescriptionRow,
                        drugs: updated
                    }
                };
            } else {
                // Update existing prescriptionList row
                const rows = [...prev.prescriptionList];
                rows[rowIndex] = {
                    ...rows[rowIndex],
                    drugs: updated
                };
                return {
                    ...prev,
                    prescriptionList: rows
                };
            }
        });
    };


    const setDeleteState = (rowIndex, subIndex) => {
        setState(prev => {
            const rows = [...prev.prescriptionList];
            rows[rowIndex] = { ...rows[rowIndex], isDeleting: true };
            if (subIndex !== null) {
                const subList = [...rows[rowIndex].drugs];
                subList[subIndex] = { ...subList[subIndex], isSubDrugDel: true }
                rows[rowIndex].drugs = subList;
            }
            return { ...prev, prescriptionList: rows };
        });
    }

    // api calls started
    const handleCompleteDelete = async (rowIndex, subIndex = null, isNewPrescription = false) => {
        if (isNewPrescription && subIndex !== null) {
            setState(prev => {
                const newDrugs = [...prev.prescriptionRow.drugs];
                newDrugs.splice(subIndex, 1);

                return {
                    ...prev,
                    prescriptionRow: {
                        ...prev.prescriptionRow,
                        drugs: newDrugs
                    }
                };
            });
            return;
        } else {
            // set deleting flag
            setDeleteState(rowIndex, subIndex)

            // separating deletedIds
            const rows = [...state.prescriptionList];
            const row = rows[rowIndex];


            const deletedIds = subIndex !== null ? row.drugs[subIndex]?.id ? [row.drugs[subIndex]?.id] : []
                : (row.drugs ?? [])?.map(item => item.id)?.filter(Boolean);

            if (deletedIds?.length === 0 && typeof rowIndex === "number" && subIndex === null) {

                setState(prev => {
                    let rows = [...prev.prescriptionList];
                    rows.splice(rowIndex, 1);
                    return { ...prev, prescriptionList: rows };
                });

                return;
            } else if (deletedIds?.length === 0 && subIndex !== null) {
                setState(prev => {
                    const rows = [...prev.prescriptionList];
                    const subList = [...rows[rowIndex].drugs];
                    subList.splice(subIndex, 1)
                    rows[rowIndex].drugs = subList;
                    return { ...prev, prescriptionList: rows };
                });
                return;
            } else {
                try {
                    const response = await RestAPIService.delete(`${Serviceurls.AYURVEDA_PRESCRIPTION_LIST}?id=${deletedIds}`);
                    if (response.data.status === "success") {
                        successMessage(response.data.message);
                        getPrescriptionData();
                    } else {
                        errorMessage(e?.response?.data?.message || e.message);
                        setDeleteState(rowIndex, subIndex)
                    }
                } catch (e) {
                    setDeleteState(rowIndex, subIndex)
                    errorMessage(e.message);
                }
            }
        }
    };

    const handleSave = (templateData = []) => {
        const { appointmentId, ipAdmissionId, clinicId, doctorId, isFromIp } = props;
        if (!isFromIp && !appointmentId) return;
        if (isFromIp && !ipAdmissionId) return;
        try {
            updateState({ isLoading: true });
            const prescriptionData = preparePrescriptionListForAPI(templateData?.length > 0 ?
                [...state.prescriptionList, ...templateData]
                : state.prescriptionList || []);
            const data = {
                pharmacy_id: state.selectedPharmacy,
                comments: state.overallComments || "",
                data: prescriptionData
            };
            if (isFromIp) {
                data.ip_admission_id = ipAdmissionId;
                data.clinic_id = clinicId;
                data.doctor_id = doctorId;
            } else {
                data.appointment_id = appointmentId;
            }

            for (const row of prescriptionData) {
                const hasMAEN =
                    row.morning ||
                    row.afternoon ||
                    row.evening ||
                    row.night;

                const hasInstructions = Boolean(row.instructions?.trim());

                if (!hasMAEN && !hasInstructions) {
                    errorMessage("Select MAEN or Enter Instructions");
                    return updateState({ isLoading: false });
                }
            }

            if (prescriptionData?.length === 0) {
                errorMessage('Select Medicine')
                updateState({ isLoading: false });
            } else {
                RestAPIService.create(data, Serviceurls.AYURVEDA_PRESCRIPTION_LIST)
                    .then((response) => {
                        if (response.data.status === 'success') {
                            updateState({ isLoading: false, openPrevious: false });
                            getPrescriptionData()
                            successMessage(response?.data?.message);
                        }
                    }).catch((error) => {
                        updateState({ isLoading: false });
                        errorMessage(error?.response?.data?.message || error.message)
                    })
            }
        } catch (error) {
            updateState({ isLoading: false });
            errorMessage(error.message)
        }
    }
    //api calls ended 

    // template popup start
    const getTemplateListData = (pharmacyId) => {
        try {
            const pharmaId = pharmacyId || state.selectedPharmacy;
            RestAPIService.getAllTwo(Serviceurls.AYURVEDA_PRESCRP_TEMPLATE)
                .then((response) => {
                    if (response?.data?.status === 'success') {
                        const filteredData = response?.data?.data?.filter(item => {
                            return item?.pharmacy_id === pharmaId;
                        });
                        updateState({
                            prescriptionTemplateList: filteredData || []
                        })
                    }
                }).catch((error) => {
                    errorMessage(error?.response?.data?.message || error.message)
                })
        } catch (error) {
            errorMessage(error.message)
        }
    }

    const postTemplate = () => {
        try {
            let data = {}
            if (state.tempType === 'Add' || state.tempType === 'rename') {
                data = {
                    id: state.selectedTemplate?.id || null,
                    pharmacy_id: state.selectedPharmacy,
                    template_name: state.templateName,
                    deleted_ids: [],
                    data: preparePrescriptionListForAPI(state.tempType === 'Add' ?
                        state.prescriptionList : state.selectedTemplate,
                        state.tempType)
                }
            }
            if (state.templateName === '') {
                errorMessage('Enter Template Name')
            } else {
                RestAPIService.create(data, Serviceurls.AYURVEDA_PRESCRP_TEMPLATE)
                    .then((response) => {
                        if (response.data.status === 'success') {
                            successMessage(response.data.message)
                            getTemplateListData(state.selectedPharmacy)
                            updateState({
                                tempType: '',
                                openPopupAddNew: false,
                                selectedTemplate: null,
                                templateName: '',
                            })
                        }
                    }).catch((error) => {
                        errorMessage(error?.response?.data?.message || error.message)
                    })
            }
        } catch (error) {
            errorMessage(error.message)
        }
    }

    const handleSelectTemplate = (selected) => {
        updateState({ selectedTemplate: selected })
        try {
            RestAPIService.getAll(`${Serviceurls.AYURVEDA_PRESCRP_TEMPLATE}?id=${selected?.id}`)
                .then((response) => {
                    if (response.data.status === "success") {
                        const Data = response.data.data || {};
                        updateState({ selectedTemplate: Data })
                        const processedData = preparePrescriptionListForAPI(Data?.data || [], "Add");
                        handleSave(processedData);
                    } else {
                        errorMessage(response.data.message)
                    }
                }).catch((error) => {
                    errorMessage(error.message)
                })
        }
        catch (e) {
            errorMessage(e.message)
        }
    }

    const closeDeletePopup = () => {
        updateState({
            deleteTemplateName: null,
            deletePopup: false
        })
    }

    const removeDataList = () => {
        try {
            updateState({ removeData: true })
            RestAPIService.delete(`${Serviceurls.AYURVEDA_PRESCRP_TEMPLATE}?id=${state.deleteTemplateName?.id || 0}`)
                .then(response => {
                    if (response.data.status === "success") {
                        updateState({
                            deletePopup: false,
                            deleteTemplateName: null,
                            openPopupAddNew: false,
                            removeData: false
                        })
                        successMessage(response?.data?.message);
                        getTemplateListData(state.selectedPharmacy)
                    }
                })
                .catch(error => {
                    if (error?.response?.data?.status === "fail") {
                        errorMessage(error?.response?.data?.message);
                        updateState({ removeData: false })
                    }
                });
        } catch (e) {
            errorMessage(e?.message);
            updateState({ removeData: false })
        }
    }

    const renderNewTemplatePopup = () => {
        try {
            let { tempType } = state
            return (
                <Box component={'div'}>
                    <Modal open={state.openPopupAddNew}>
                        <Box className='eMed_Doc_Notes_Test_Trans_Pop_Up'>
                            <Box component={'div'} display={'flex'} justifyContent={'space-between'} alignItems={'center'} p={'0.5vw'}>
                                <Typography fontWeight={600} color={Colors.SecondaryText}>{tempType === 'Add' ? 'Add New Template' : tempType === 'rename' ? 'Rename Template Name' : 'Delete Template'}</Typography>
                            </Box>
                            <Box component={'div'}>
                                <Box component={'div'} display={'flex'} justifyContent={'center'} alignItems={"center"} gap={"0.5vw"}>
                                    {renderSelect('Select Pharmacy', "selectedPharmacy", state.pharmacyList,
                                        "12dvw", "pharmacy_name", "pharmacy_id",
                                        state.prescriptionList?.length > 0 && state.prescriptionId && state.selectedPharmacy)
                                    }
                                    {renderTextField('Enter Template Name', "templateName", 75)}
                                </Box>

                                <Box component={'div'} display={'flex'} justifyContent={'center'} mt={'1vw'}>
                                    <Button variant='contained'
                                        sx={{ width: '5vw', height: '2vw', textTransform: 'capitalize', mr: '1vw' }}
                                        onClick={() => { postTemplate() }}
                                    >Add</Button>
                                    <Button variant='outlined'
                                        sx={{ width: '5vw', height: '2vw', textTransform: 'capitalize' }}
                                        onClick={() => updateState({ tempType: '', openPopupAddNew: false, templateName: "" })}
                                    >Close</Button>
                                </Box>
                            </Box>
                        </Box>
                    </Modal>
                </Box>
            )
        } catch (error) {
            errorMessage(error.message)
        }
    }
    //template popup end

    // recall popup start
    const renderRecallPopup = () => {
        return (
            <Dialog
                open={state.recallPopup}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '0.5vw', marginBottom: '0vw' }}>
                    <img className="eMed_DeletePopup_CrossIcon" src={ImagePaths.LabCloseButton.default} alt='Close' onClick={() => { updateState({ recallPopup: false, recallReason: '', recallError: '' }) }} />
                </div>
                <DialogTitle id="alert-dialog-title"> <Typography component="div">  Recall Drug </Typography>  </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontSize: '0.8vw' }} id="alert-dialog-description">
                        Please provide a reason for recalling this prescription.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        multiline
                        rows={3}
                        style={{ marginTop: '1vw' }}
                        fullWidth
                        variant="outlined"
                        label="Reason for Recall*"
                        value={state.recallReason || ''}
                        onChange={(e) => {
                            setState(prev => ({
                                ...prev,
                                recallReason: e.target.value,
                                recallError: e.target.value.trim() ? '' : 'Reason is required'
                            }))
                        }}
                        inputProps={{ maxLength: 150 }}
                        error={!!state.recallError}
                        helperText={state.recallError}
                        placeholder="Enter the reason for recalling this prescription..."
                    />
                </DialogContent>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: "center", justifyContent: "flex-end" }}>
                    <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button size="small" variant="outlined" sx={{ textTransform: "capitalize" }} onClick={() => { updateState({ recallPopup: false, recallReason: '', recallError: '' }) }}>
                            {"Close"}
                        </Button>
                        <Button size="small" variant="contained" sx={{ textTransform: "capitalize" }} onClick={() => {
                            if (!state.recallReason?.trim()) {
                                setState(prev => ({
                                    ...prev,
                                    recallError: ''
                                }));
                                return;
                            }
                            postPrescriptionTransmit(true)
                        }}
                            disabled={!state.recallReason?.trim()}>
                            {"Recall"}
                        </Button>
                    </DialogActions>
                </div>
            </Dialog>
        )
    }
    // recall popup end

    // previous prescription popup start

    const getPreviousPrescriptionData = () => {
        const { appointmentId, patientId, ipAdmissionId, clinicId, doctorId, isFromIp } = props;
        if (!patientId) return;
        if (!isFromIp && !appointmentId) return;
        if (isFromIp && !ipAdmissionId) return;
        try {
            const params = isFromIp ? `?patient_id=${patientId}&ip_admission_id=${ipAdmissionId}&clinic_id=${clinicId}&doctor_id=${doctorId}` : `?patient_id=${patientId}&appointment_id=${appointmentId}`;
            RestAPIService.getAll(Serviceurls.AYURVEDA_PREVIOUS_PRESCP_HISTORY + params)
                .then((response) => {
                    if (response?.data?.status === 'success') {
                        const Data = response?.data?.data?.length > 0 ? response.data.data : [];
                        updateState({
                            openPrevious: true,
                            previousPrescripDate: Data,
                            selectedPrevDate: Data[0]?.date,
                            prevPrescripData: prepareAddKeyList(Data[0]?.data) || [],
                            prevAppointmentId: Data[0]?.appointment_id || null,
                        })
                    }
                }).catch((error) => {
                    errorMessage(error?.response?.data?.message || error.message)
                })
        } catch (error) {
            errorMessage(error.message)
        }
    }

    const copyPreviousPrescription = () => {
        try {
            const processedData = preparePrescriptionListForAPI(state.prevPrescripData, "Add")
            if (processedData?.length > 0) {
                handleSave(processedData)
            }
        } catch (error) {
            errorMessage(error.message)
        }
    }

    const renderPreviousPrescriptionPopup = () => {
        try {
            return (
                <Box component={'div'}>
                    <Modal open={state.openPrevious}>
                        <Box component={'div'} className='eMed_Doc_Notes_Counsellor_Pop_Up' sx={{ width: "fit-content" }}>
                            <Box component={'div'} display={'flex'} justifyContent={'space-between'} mt={'0.5vw'}>
                                <Typography pl={'0.5vw'} fontWeight={600} color={Colors.SecondaryText}>Previous Prescription</Typography>
                                <Button
                                    onClick={() => { updateState({ openPrevious: false }) }}
                                >
                                    <Box component={'img'} src={ImagePaths.LabCloseButton.default} height={'1.1vw'} width={'1.1vw'} />
                                </Button>
                            </Box>
                            <Box component={'div'} display={'flex'} m={'0.5vw'} alignItems={'center'}>
                                <Autocomplete
                                    size='small'
                                    clearIcon
                                    sx={{ width: '15vw', mr: '1vw' }}
                                    options={state.previousPrescripDate}
                                    getOptionLabel={(option) => typeof (option) === 'string' ? formatDate(option) : formatDate(option?.date)}
                                    value={state.selectedPrevDate}
                                    onChange={(e, value) => {
                                        updateState({
                                            selectedPrevDate: value?.date,
                                            prevPrescripData: value?.data || [],
                                            prevAppointmentId: value?.appointment_id,
                                        })
                                    }}
                                    renderInput={(params) => <TextField {...params} label={'Select Date'} />}
                                />
                                <Button variant='contained'
                                    sx={{ textTransform: 'capitalize', mr: '1vw' }}
                                    onClick={() => getPrescriptionPrint(state.prevAppointmentId || null)}
                                    disabled={state.previousPrescripDate?.length > 0 ? false : true}
                                >Print</Button>
                                <Button variant='contained'
                                    sx={{ textTransform: 'capitalize' }}
                                    onClick={() => {
                                        if (state.match_generic_list?.length > 0) {
                                            updateState({ addPath: 'copy', hasAllergy: true })
                                            setAllergyPopup(true)
                                        } else {
                                            copyPreviousPrescription()
                                        }
                                    }}
                                    disabled={state.isLocked || props.isLocked || (state.previousPrescripDate?.length > 0 ? false : true)}
                                >Copy</Button>
                            </Box>
                            <Box component={'div'} sx={{ height: '21vw', m: "0.5vw", overflow: 'auto', border: '1px solid lightgray' }}>
                                <Box sx={{ width: "100%" }}>
                                    <Table size="small" stickyHeader aria-label="medication table"
                                        sx={{ maxHeight: "28vw", overflowY: "auto", borderCollapse: "collapse", tableLayout: "fixed" }}>

                                        {/* ---------- STICKY HEADER ---------- */}
                                        <TableHead>
                                            <TableRow>
                                                {headers?.slice(0, -1)?.map((item, idx) => (
                                                    <TableCell
                                                        key={idx}
                                                        align={(idx === 2) ? "center" : "left"}
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: "0.9vw",
                                                            color: Colors.SecondaryText,
                                                            backgroundColor: Colors.Background,
                                                            width: colWidths[idx],
                                                        }}
                                                    >
                                                        {item}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>

                                        {/* ---------- BODY ---------- */}
                                        <TableBody> {renderTableBody(state.prevPrescripData || [])}</TableBody>
                                    </Table>
                                </Box>
                            </Box>
                        </Box>
                    </Modal>
                </Box>
            )
        } catch (error) {
            errorMessage(error.message)
        }
    }
    // previous prescription popup end

    // transmit functionality start
    const handleTransmit = () => {
        updateState({
            isTransClked: true,
        })
    };

    const postPrescriptionTransmit = (recalled = false) => {
        const { appointmentId, ipAdmissionId, isFromIp } = props;
        if (!isFromIp && !appointmentId) return;
        if (isFromIp && !ipAdmissionId) return;
        try {
            const data = { id: state.prescriptionId };
            RestAPIService[recalled ? "updateWithOutId" : "create"](data, Serviceurls.AYURVEDA_PRESCRIP_TRANSMIT)
                .then((response) => {
                    if (response.data.status === 'success') {
                        let smsConfig = localGetItem("smsConfig") ? JSON.parse(localGetItem("smsConfig")) : {}
                        if (!recalled && smsConfig?.prescription_sms) {
                            updateState({
                                successPopup: true,
                                successPopupTxt: response.data.message,
                                smsSuccessTxt: response.data.sms_message,
                                recallReason: '',
                                recallError: ''
                            })
                        } else {
                            successMessage(response.data.message)
                            updateState({
                                recallReason: '',
                                recallError: ''
                            })
                        }
                        updateState({
                            transmitted: !recalled,
                            recallPopup: false,
                            isTransClked: false,
                        })
                        getPrescriptionData()
                    }
                }).catch((error) => {
                    errorMessage(error?.response?.data?.message || error.message)
                })
        } catch (error) {
            errorMessage(error.message)
        }
    }

    // transmit functionality end

    // success popup Start
    const renderSuccessPopup = () => {
        return (
            <div>
                <Modal open={true}>
                    <Box component={'div'} className="eMed_Bill_Success_Preview_Main" height={'30vh'}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2vw' }}>
                            <Box sx={{ display: 'flex', gap: '1vw' }}>
                                <img src={ImagePaths.Tick.default} alt='success' />
                                <Typography fontWeight={600}>{state.successPopupTxt ? state.successPopupTxt : 'Transmitted Successfully'}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: '1vw' }}>
                                {state.smsSuccessTxt ? <ErrorIcon color='error' /> : <img src={ImagePaths.Tick.default} alt='success' />}
                                <Typography fontWeight={600}>{state.smsSuccessTxt ? state.smsSuccessTxt : 'SMS Sent Successfully'}</Typography>
                            </Box>
                        </Box>
                        <Box display={'flex'} gap={'0.5vw'}>
                            <Button sx={{ textTransform: 'capitalize', }} size="small" variant="outlined" onClick={() => { updateState({ successPopup: false }) }}>{"OK"}</Button>
                        </Box>
                    </Box>
                </Modal>
            </div>
        )
    }
    //sucess popup end

    const renderTableBody = (List = []) => {
        return (<>
            {List?.map((row, rowIndex) => {
                const subDrugs = row.drugs;
                const subDrugsToRender = row.showComments
                    ? (subDrugs.length >= 2 ? subDrugs : [...subDrugs, null])
                    : subDrugs;
                const durationMode = duration_type?.find(item => item?.value === row?.duration_mode)?.label || "Days";
                const instruction = instructionList?.find(item => item?.value === row?.instructions)?.label || "";
                return (
                    <React.Fragment key={rowIndex}>
                        {subDrugsToRender?.map((item, subIndex) => {
                            const isFirst = subIndex === 0;
                            return (
                                <TableRow key={subIndex} sx={{ borderLeft: `5px solid ${rowIndex % 2 === 0 ? "gray" : "#4da3d4"}` }}>

                                    {/* Medicine Name */}
                                    <TableCell sx={{ width: colWidths[0] }}>{item ? ContentInTooltip(item?.drug_name, 15, '0.9vw') : null} </TableCell>

                                    {/* UOM */}
                                    <TableCell sx={{ width: colWidths[1], borderRight: "1px solid rgba(224, 224, 224, 1)" }}>{item ? ContentInTooltip(`${item?.quantity} ${item?.uom}`, 10, '0.9vw') : null} </TableCell>
                                    {isFirst && item && (<>
                                        {/* MAEN*/}
                                        <TableCell sx={{ width: colWidths[2], }}>
                                            {<Box display={"flex"} alignItems={"center"} gap={"0.3vw"}>
                                                {renderCheckBox("morning", rowIndex, row?.morning || false)}
                                                {renderCheckBox("afternoon", rowIndex, row?.afternoon || false)}
                                                {renderCheckBox("evening", rowIndex, row?.evening || false)}
                                                {renderCheckBox("night", rowIndex, row?.night || false)}
                                            </Box>}
                                        </TableCell>

                                        {/* Instruction */}
                                        <TableCell sx={{ width: colWidths[4] }}>{ContentInTooltip(instruction || "-", 15, '0.9vw')}</TableCell>

                                        {/* Duration */}
                                        <TableCell sx={{ width: colWidths[6] }}>{ContentInTooltip(`${row?.duration} ${durationMode}`, 15, '0.9vw')}</TableCell>
                                    </>)}

                                    {/* COMMENTS COLUMN (full width for remaining rows) */}
                                    {(
                                        <>
                                            {row.showComments &&  (
                                                <TableCell colSpan={6} rowSpan={subDrugsToRender.length - 1 || 1}>
                                                    {ContentInTooltip(row?.comments, 50, '0.9vw')}
                                                </TableCell>
                                            )}
                                        </>
                                    )}
                                </TableRow>
                            );
                        })}
                    </React.Fragment>
                );
            })}
        </>)
    }

    const disableBtn = useMemo(() => {
        return props.isLocked || state.transmitted
    }, [props.isLocked, state.transmitted]);

    const disableIconBtn = useMemo(() => {
        return state.isLoading || disableBtn || state.recalled === true
    }, [state.isLoading, disableBtn, state.recalled])

    return (
        <ShowComponents onIntersection={getApiCall.bind(this)}>
            <Card sx={{
                width: "100%", height: "fit-content", padding: "0.3vw",
                marginBottom: "0.5vw", border: `2px solid ${props.isSelected ? Colors.themeDark : Colors.white}`
            }}>
                <Box display={"flex"} flexDirection={'row'} alignItems={"center"} justifyContent={'space-between'} padding={"0.5vw"} borderBottom={`1px solid #E3E2E2`}>
                    <Box display={"flex"} flexDirection={'row'} alignItems={"center"} columnGap={"0.5vw"}>
                        {renderTitle(Title || "Prescription", props.isSelected)}
                        {state.prescriptionId && renderIconButton('Transmit',
                            (disableBtn || state.isTransClked) ? ImagePaths.medicationTransmitOff.default : ImagePaths.medicationTransmitOn.default,
                            () => handleTransmit(), disableBtn || state.isTransClked)}
                        {renderIconButton('Previous Prescription', ImagePaths.PreviousPrescription.default, () => getPreviousPrescriptionData(), disableBtn)}
                        {/* {renderIconButton('Favourite', ImagePaths.StarUnfill.default, () => { updateState({ openFav: true }) }, disableBtn)} */}
                        {(state.Recalled === false && state.transmitted && !props.isLocked) ?
                            <Button
                                className='Common_Btn_Min_Width'
                                onClick={() => { updateState({ recallPopup: true }) }}
                            >
                                <Tooltip placement='top' title='Recall' arrow>
                                    <div style={{ backgroundColor: "#b8170d", borderRadius: "0.2vw" }}>
                                        <Box component={'img'} src={ImagePaths.Recall.default} height={'1.7vw'} width={'1.7vw'} />
                                    </div>
                                </Tooltip>
                            </Button>
                            : null}
                    </Box>
                    <Box display={"flex"} flexDirection={'row'} alignItems={"center"} columnGap={"0.5vw"}>
                        {renderSelect('Select Pharmacy', "selectedPharmacy", state.pharmacyList,
                            "12dvw", "pharmacy_name", "pharmacy_id",
                            state.prescriptionList?.length > 0 && state.prescriptionId && state.selectedPharmacy)
                        }
                        <Autocomplete
                            size='small'
                            disabled={disableBtn}
                            sx={{ width: '13vw' }}
                            options={state.prescriptionTemplateList}
                            getOptionLabel={(option) => option.template_name || ''}
                            value={state.selectedTemplate}
                            autoComplete='off'
                            onChange={(event, newValue, reason) => {
                                if (reason === "clear") return;
                                handleSelectTemplate(newValue);
                            }}
                            renderOption={(props, option) => {
                                return (
                                    <li {...props} style={{ padding: "0vh 1.2vh" }}>
                                        <ListItem sx={{ display: 'flex', justifyContent: 'space-between', padding: 0 }}>
                                            <ListItemText primary={option?.template_name && option?.template_name?.length > 14 ?
                                                <Tooltip placement='top' title={option?.template_name}>
                                                    {<div>{option?.template_name?.slice(0, 14) + '...'}</div>}
                                                </Tooltip> : option?.template_name} />
                                            <IconButton
                                                edge="end"
                                                color="error"
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    updateState({
                                                        tempType: 'delete',
                                                        deleteTemplateName: option,
                                                        deletePopup: true
                                                    });
                                                }}
                                            >
                                                <Tooltip placement='top' title={"Delete"} arrow>
                                                    <DeleteIcon sx={{ height: "2.5vh", width: "2.5vw" }} /> </Tooltip>
                                            </IconButton>
                                        </ListItem>
                                    </li>
                                );
                            }}
                            renderInput={(params) => <TextField {...params} label='Select Template' />}
                            PaperComponent={({ children }) => {
                                return (
                                    <Paper component={'div'}>
                                        {children}
                                        <Button
                                            disabled={!state.prescriptionId}
                                            color="primary"
                                            fullWidth
                                            sx={{ justifyContent: "flex-start", pl: 2, textTransform: 'capitalize' }}
                                            onMouseDown={(e) => {
                                                updateState({ openPopupAddNew: true, tempType: 'Add' })
                                            }}
                                        >
                                            Save as Template
                                        </Button>
                                    </Paper>
                                )
                            }}
                        />
                        {/* {renderSelect("", "selectedLanguage", language, "10vw")} */}
                        {renderIconButton('Print', ImagePaths.PrintIcons, () => getPrescriptionPrint(), false, "2vw")}
                        <Button variant="contained" color="primary" disabled={state.isLoading || disableBtn || state.recalled === true} onClick={handleSave}> SAVE </Button>
                    </Box>
                </Box>
                <Box sx={{ width: "100%" }}>
                    <Table size="small" stickyHeader aria-label="medication table"
                        sx={{ maxHeight: "28vw", overflowY: "auto", borderCollapse: "collapse", tableLayout: "fixed" }}>

                        {/* ---------- STICKY HEADER ---------- */}
                        <TableHead>
                            <TableRow>
                                {headers?.map((item, idx) => (
                                    <TableCell
                                        key={idx}
                                        align={(idx === headers.length - 1 || idx === 2) ? "center" : "left"}
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "0.9vw",
                                            color: Colors.SecondaryText,
                                            backgroundColor: Colors.Background,
                                            width: colWidths[idx],
                                        }}
                                    >
                                        {item}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        {/* ---------- BODY ---------- */}
                        <TableBody>
                            {state.prescriptionRow?.drugs?.map((item, subIndex) => {
                                const isFirst = subIndex === 0;
                                return (
                                    <React.Fragment key={`new-drug-${subIndex}`}>
                                        <TableRow sx={{ marginBottom: "0.625vw" }}>

                                            {/* Medicine Name */}
                                            <TableCell sx={{ padding: cellPadding, width: colWidths[0] }}>
                                                {item ? renderAutoComplete("Search Medicine", "100%", null, subIndex, true) : null}
                                            </TableCell>

                                            {/* UOM */}
                                            <TableCell sx={{ padding: cellPadding, width: colWidths[1], borderRight: "1px solid rgba(224, 224, 224, 1)" }}>
                                                {item ? (<Box display={"flex"} alignItems={"center"}>
                                                    <Box display={"flex"} alignItems={"center"}>
                                                        {renderTextFieldRow("", "quantity", "4vw", null, subIndex, true)}
                                                        {renderSelectRow("", "uom", uomUnitOptions, "3.5vw", "label", "value", null, subIndex, false, true)}
                                                    </Box>
                                                    {isFirst ? renderIconButton("Add", item?.isSubDrug ? ImagePaths.DisabledAddIcon.default : ImagePaths.AddIcon.default,
                                                        () => { handleAddSubDrug(null, true) }, (item.isSubDrug || disableIconBtn), "1.5vw")
                                                        : renderIconButton("Delete", item?.isSubDrug ? ImagePaths.DisabledDelete.default : ImagePaths.LabDeleteButton.default,
                                                            () => { handleCompleteDelete(null, subIndex, true) }, (item.isSubDrugDel || disableIconBtn), "1.5vw")}
                                                </Box>) : null}
                                            </TableCell>
                                            {isFirst && item && (<>
                                                {/* MAEN*/}
                                                <TableCell sx={{ padding: cellPadding, width: colWidths[2], }}>
                                                    {<Box display={"flex"} alignItems={"center"} gap={"0.3vw"}>
                                                        {renderCheckBox("morning", null, null, true)}
                                                        {renderCheckBox("afternoon", null, null, true)}
                                                        {renderCheckBox("evening", null, null, true)}
                                                        {renderCheckBox("night", null, null, true)}
                                                    </Box>}
                                                </TableCell>

                                                {/* Instruction */}
                                                <TableCell sx={{ padding: cellPadding, width: colWidths[4] }}>
                                                    {renderSelectRow("Instruction *", "instructions", instructionList, "100%", "label", "value", null, null, false, true)}
                                                </TableCell>

                                                {/* Duration */}
                                                <TableCell sx={{ padding: cellPadding, width: colWidths[8] }}>
                                                    {(
                                                        <Box display={"flex"} alignItems={"center"}>
                                                            {renderTextFieldRow("Duration", "duration", "4dvw", null, null, true)}
                                                            {renderSelectRow("", "duration_mode", duration_type, "8dvw", "label", "value", null, null, false, true)}
                                                        </Box>
                                                    )}
                                                </TableCell>

                                                {/* Action */}
                                                <TableCell sx={{ padding: cellPadding, width: colWidths[7], textAlign: "center" }}>
                                                    {(
                                                        <Box display={"flex"} alignItems={"center"} gap={"0vw"}>
                                                            {/* {renderIconButton("Comments", state.prescriptionRow.showComments ? ImagePaths.BillingOff.default : ImagePaths.BillingOn.default,
                                                                () => { handleChange("showComments", true, null, null, false, true) }, (state.prescriptionRow.showComments || disableIconBtn), "2vw")} */}
                                                            {renderIconButton("Add", state.prescriptionRow.isAdd ? ImagePaths.DisabledAddIcon.default : ImagePaths.AddIcon.default,
                                                                () => handleAddToPrescriptionList(), (state.prescriptionRow.isAdd || disableIconBtn), "1.5vw")}
                                                        </Box>
                                                    )}
                                                </TableCell>
                                            </>)}

                                            {/* COMMENTS COLUMN (full width for remaining rows) */}
                                            {(
                                                <>
                                                    {state.prescriptionRow.showComments && (
                                                        <TableCell colSpan={6} rowSpan={state.prescriptionRow?.drugs?.length - 1 || 1} sx={{ padding: cellPadding }}>
                                                            <TextField
                                                                fullWidth
                                                                value={state.prescriptionRow.comments || ""}
                                                                onChange={(e) => { handleChange("comments", e.target.value, null, null, false, true) }}
                                                                multiline
                                                                maxRows={3}
                                                                placeholder="Comments Here"
                                                                size="small"
                                                                inputProps={{ maxLength: 1000, style: { overflow: "auto" } }}
                                                            />
                                                        </TableCell>
                                                    )}
                                                </>
                                            )}
                                        </TableRow>
                                    </React.Fragment>

                                );
                            })}
                            {state?.prescriptionList?.length > 0 ?
                                (state.prescriptionList?.map((row, rowIndex) => {
                                    const subDrugs = row.drugs;
                                    const subDrugsToRender = row.showComments
                                        ? (subDrugs.length >= 2 ? subDrugs : [...subDrugs, null])
                                        : subDrugs;
                                    const rowId = row.id || `row-${rowIndex}`;
                                    return (
                                        <React.Fragment key={rowIndex}>
                                            {subDrugsToRender?.map((item, subIndex) => {
                                                const isFirst = subIndex === 0;
                                                const drugItemId = item?.id || `drug-${subIndex}`;
                                                return (
                                                    <TableRow key={`${rowId}-${drugItemId}`} sx={{ borderLeft: `5px solid ${rowIndex % 2 === 0 ? "gray" : "#4da3d4"}` }}>

                                                        {/* Medicine Name */}
                                                        <TableCell sx={{ padding: cellPadding, width: colWidths[0] }}>
                                                            {item ? renderAutoComplete("Search Medicine", "100%", rowIndex, subIndex) : null}
                                                        </TableCell>

                                                        {/* UOM */}
                                                        <TableCell sx={{ padding: cellPadding, width: colWidths[1], borderRight: "1px solid rgba(224, 224, 224, 1)" }}>
                                                            {item ? (<Box display={"flex"} alignItems={"center"}>
                                                                <Box display={"flex"} alignItems={"center"}>
                                                                    {renderTextFieldRow("", "quantity", "4vw", rowIndex, subIndex)}
                                                                    {renderSelectRow("", "uom", uomUnitOptions, "3.5vw", "label", "value", rowIndex, subIndex)}
                                                                </Box>
                                                                {isFirst ? renderIconButton("Add", item?.isSubDrug ? ImagePaths.DisabledAddIcon.default : ImagePaths.AddIcon.default,
                                                                    () => { handleAddSubDrug(rowIndex, subIndex) }, (item.isSubDrug || disableIconBtn), "1.5vw")
                                                                    : renderIconButton("Delete", item?.isSubDrug ? ImagePaths.DisabledDelete.default : ImagePaths.LabDeleteButton.default,
                                                                        () => { handleCompleteDelete(rowIndex, subIndex) }, (item.isSubDrugDel || disableIconBtn), "1.5vw")}
                                                            </Box>) : null}
                                                        </TableCell>
                                                        {isFirst && item && (<>
                                                            {/* MAEN*/}
                                                            <TableCell sx={{ padding: cellPadding, width: colWidths[2], }}>
                                                                {<Box display={"flex"} alignItems={"center"} gap={"0.3vw"}>
                                                                    {renderCheckBox("morning", rowIndex)}
                                                                    {renderCheckBox("afternoon", rowIndex)}
                                                                    {renderCheckBox("evening", rowIndex)}
                                                                    {renderCheckBox("night", rowIndex)}
                                                                </Box>}
                                                            </TableCell>

                                                            {/* Instruction */}
                                                            <TableCell sx={{ padding: cellPadding, width: colWidths[4] }}>
                                                                {renderSelectRow("Instruction *", "instructions", instructionList, "100%", "label", "value", rowIndex, null, false, false)}
                                                            </TableCell>

                                                            {/* Duration */}
                                                            <TableCell sx={{ padding: cellPadding, width: colWidths[8] }}>
                                                                {(
                                                                    <Box display={"flex"} alignItems={"center"}>
                                                                        {renderTextFieldRow("Duration", "duration", "4dvw", rowIndex)}
                                                                        {renderSelectRow("", "duration_mode", duration_type, "8dvw", "label", "value", rowIndex, null, false, false)}
                                                                    </Box>
                                                                )}
                                                            </TableCell>

                                                            {/* Action */}
                                                            <TableCell sx={{ padding: cellPadding, width: colWidths[7], textAlign: "center" }}>
                                                                {(
                                                                    <Box display={"flex"} alignItems={"center"} gap={"0vw"}>
                                                                        {/* {renderIconButton("Comments", row.showComments ? ImagePaths.BillingOff.default : ImagePaths.BillingOn.default,
                                                                            () => { handleChange("showComments", true, rowIndex) }, (row.showComments || disableIconBtn), "2vw")} */}
                                                                        {renderIconButton("Delete", row.isDeleting ? ImagePaths.DisabledDelete.default : ImagePaths.LabDeleteButton.default,
                                                                            async () => await handleCompleteDelete(rowIndex), (row.isDeleting || disableIconBtn), "1.5vw")}
                                                                    </Box>
                                                                )}
                                                            </TableCell>
                                                        </>)}
                                                        {/* COMMENTS COLUMN (full width for remaining rows) */}
                                                        {(
                                                            <>
                                                                {row.showComments && subIndex === 1 && (
                                                                    <TableCell colSpan={6} rowSpan={subDrugsToRender.length - 1 || 1} sx={{ padding: cellPadding }}>
                                                                        <TextField
                                                                            fullWidth
                                                                            value={row.comments || ""}
                                                                            onChange={(e) => { handleChange("comments", e.target.value, rowIndex) }}
                                                                            multiline
                                                                            maxRows={3}
                                                                            placeholder="Comments Here"
                                                                            size="small"
                                                                            inputProps={{ maxLength: 500, style: { overflow: "auto" } }}
                                                                        />
                                                                    </TableCell>
                                                                )}
                                                            </>
                                                        )}
                                                    </TableRow>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                })) : <TableRow>
                                    <TableCell colSpan={headers.length} align="center">
                                        No Records To Be Shown
                                    </TableCell>
                                </TableRow>}
                        </TableBody>
                    </Table>
                </Box>
                <Box sx={{ width: "100%", margin: "1vw 0vw" }}>
                    {renderTitle("Comments")}
                    <ToolsFieldWithMic
                        ref={editorRef}
                        Title={""}
                        EditorRef={editorRef}
                        saveData={handleEditorSave}
                        isFrom="prescription"
                        minHeight={"15vh"}
                        inputProps={{ maxLength: 500 }}
                        overallHeight={"auto"}
                        stateName="overallComments"
                    />
                </Box>
            </Card >
            {renderNewTemplatePopup()}
            {renderRecallPopup()}
            {renderPreviousPrescriptionPopup()}
            {state.successPopup && renderSuccessPopup()}
            {
                state.deletePopup &&
                <DeletePopup
                    DeletTitle={`Are you sure you want to delete  ${state.deleteTemplateName?.template_name || "-"} ?`}
                    deleteAlertPopupClose={closeDeletePopup.bind(this)}
                    removeData={removeDataList.bind(this)}
                    disable={state.removeData}
                />
            }
        </ShowComponents >
    )
}

export default DocPrescription